import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "../lib/api";
import {
  format, subDays, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, subWeeks, subMonths,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  Package, Calendar, ArrowUpDown, ArrowUp, ArrowDown,
  Search, Pencil, AlertTriangle, Info, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { API } from "../App";

// ─── Formatters ──────────────────────────────────────────────────────────────
const isNeg  = (v) => typeof v === "number" && v < 0;
const fmtEur = (v) => `€${(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })}`;
const fmtPct = (v) => (v == null ? "—" : `${v.toLocaleString("es-ES", { minimumFractionDigits: 2 })}%`);
const fmtX   = (v) => (v == null ? "—" : `${v.toLocaleString("es-ES", { minimumFractionDigits: 2 })}x`);
const fmtInt = (v) => (v || 0).toLocaleString("es-ES");

// ─── Rentabilidad Real (uses distributed spend = all platforms proportional) ─
// avgMargin: margen bruto medio de la tienda (0..1) para estimar coste de cross-sell
function calcRentReal(row, avgMargin) {
  const rev  = row.revenue || 0;
  const cs   = row.cross_sell_revenue || 0;
  const cost = row.product_cost || 0;
  const ad   = row.distributed_ad_spend ?? row.ad_spend ?? 0;
  const tot  = rev + cs;
  if (tot <= 0) return { value: null, hasCrossSell: cs > 0 };
  // Beneficio bruto directo + estimación de beneficio cross-sell (coste descontado)
  const grossProfit     = rev - cost;
  const csProfitEst     = cs * (avgMargin ?? 0);
  return {
    value: parseFloat(((grossProfit + csProfitEst - ad) / tot * 100).toFixed(2)),
    hasCrossSell: cs > 0,
  };
}

function RentReal({ row, avgMargin }) {
  const { value, hasCrossSell } = calcRentReal(row, avgMargin);
  if (value == null) return <span className="text-slate-400 font-mono">—</span>;
  const cls = value >= 20 ? "text-green-700 font-semibold"
    : value >= 0 ? "text-amber-600"
    : "text-red-600 font-semibold";
  return (
    <span className={`inline-flex items-center gap-1 font-mono ${cls}`}>
      {fmtPct(value)}
      {!hasCrossSell && (
        <span title="Sin datos de ventas cruzadas para este período" className="text-slate-400 cursor-help">
          <Info className="w-3 h-3" />
        </span>
      )}
    </span>
  );
}

// ─── Sort / URL helpers ───────────────────────────────────────────────────────
const SORT_FIELDS = new Set([
  "reference","name","visits","add_to_cart","sales","conversion_rate",
  "revenue","product_cost","gross_profit","gross_margin_pct","ad_spend",
  "net_profit","net_margin_pct","roas",
]);
function getInitialDate(search) {
  const p = new URLSearchParams(search);
  if (p.get("preset") === "last30") return { from: subDays(new Date(), 29), to: new Date() };
  return { from: null, to: null };
}
function getInitialSort(search) {
  const p = new URLSearchParams(search);
  const s = p.get("sort_by");
  const o = p.get("sort_order");
  if (s && SORT_FIELDS.has(s)) return { sortBy: s, sortOrder: o === "asc" ? "asc" : "desc" };
  return { sortBy: "net_profit", sortOrder: "desc" };
}

const COLUMNS = [
  { key: "reference",           label: "Referencia",            align: "left" },
  { key: "name",                label: "Nombre",                align: "left" },
  { key: "visits",              label: "Visitas" },
  { key: "add_to_cart",         label: "ATC" },
  { key: "sales",               label: "Ventas" },
  { key: "conversion_rate",     label: "% Conv." },
  { key: "revenue",             label: "Ingresos" },
  { key: "product_cost",        label: "Coste Producto" },
  { key: "gross_profit",        label: "Bfo. Bruto" },
  { key: "gross_margin_pct",    label: "Margen Bruto %" },
  { key: "ad_spend",            label: "Gasto GAds (real)" },
  { key: "distributed_ad_spend",label: "Gasto Total Dist.", sortable: false },
  { key: "net_profit",          label: "Bfo. Neto" },
  { key: "net_margin_pct",      label: "Margen Neto %" },
  { key: "roas",                label: "ROAS" },
  { key: "cross_sell_conversions", label: "Vtas. Cruzadas" },
  { key: "cross_sell_revenue",  label: "Ing. Cruzados" },
  { key: "true_roas",           label: "ROAS Real" },
  { key: "halo_score",          label: "Halo Score" },
  { key: "_rent_real",          label: "Rentabilidad Real %", special: true },
];
const TOTAL_COLS = COLUMNS.length;

export default function AnalyticsProductsPage() {
  const location = useLocation();
  const [dateRange, setDateRange]       = useState(() => getInitialDate(location.search));
  const [pendingDate, setPendingDate]   = useState(() => getInitialDate(location.search));
  const [calOpen, setCalOpen]           = useState(false);
  const [search, setSearch]             = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows]                 = useState([]);
  const [pagination, setPagination]     = useState({ page: 1, page_size: 50, total_rows: 0, total_pages: 1 });
  const [sort, setSort]                 = useState(() => getInitialSort(location.search));
  const [loading, setLoading]           = useState(false);
  const [dataQuality, setDataQuality]   = useState({ excluded_sales_lines: 0, unmatched_google_ads_cost: 0 });
  const [adSpendSrc, setAdSpendSrc]     = useState(null);
  const [sources, setSources]           = useState({ visits: "none", add_to_cart: "none", sales: "order_lines" });
  const [repairModal, setRepairModal]   = useState({ open: false, product: null, newCost: "", saving: false });
  const [avgStoreGrossMargin, setAvgStoreGrossMargin] = useState(null);

  // Zero-price alert: catalog health check (independent of date range)
  const [zeroPriceData, setZeroPriceData]   = useState(null);  // null = loading, {count, products}
  const [zeroPriceOpen, setZeroPriceOpen]   = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    axios.get(`${API}/analytics/products/zero-price-alert`, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => setZeroPriceData(r.data))
      .catch(() => setZeroPriceData({ count: 0, products: [] }));
  }, []);

  const hasRange = Boolean(dateRange.from && dateRange.to);

  const presets = useMemo(() => [
    { label: "Hoy",            from: new Date(),                                       to: new Date() },
    { label: "Ayer",           from: subDays(new Date(), 1),                           to: subDays(new Date(), 1) },
    { label: "Últimos 7 días", from: subDays(new Date(), 6),                           to: new Date() },
    { label: "Últimos 30 días",from: subDays(new Date(), 29),                          to: new Date() },
    { label: "Este mes",       from: startOfMonth(new Date()),                         to: new Date() },
    { label: "Mes pasado",     from: startOfMonth(subMonths(new Date(), 1)),           to: endOfMonth(subMonths(new Date(), 1)) },
    { label: "Esta semana",    from: startOfWeek(new Date(), { weekStartsOn: 1 }),     to: new Date() },
    { label: "Semana pasada",  from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) },
  ], []);

  // Debounce search to avoid firing on every keystroke
  useEffect(() => {
    const t = setTimeout(() => { setPagination((p) => ({ ...p, page: 1 })); setDebouncedSearch(search); }, 500);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = async (page = pagination.page) => {
    if (!dateRange.from || !dateRange.to) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${API}/analytics/products-profitability`, {
        params: {
          start_date: format(dateRange.from, "yyyy-MM-dd"),
          end_date:   format(dateRange.to,   "yyyy-MM-dd"),
          search: debouncedSearch.trim() || undefined,
          sort_by: sort.sortBy, sort_order: sort.sortOrder,
          page, page_size: 50,
        },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setRows(res.data.rows || []);
      setPagination(res.data.pagination || { page: 1, page_size: 50, total_rows: 0, total_pages: 1 });
      setDataQuality(res.data.data_quality || {});
      setAdSpendSrc(res.data?.sources?.ad_spend || null);
      setSources(res.data?.sources?.analytics || { visits: "none", add_to_cart: "none", sales: "order_lines" });
      setAvgStoreGrossMargin(res.data.avg_store_gross_margin ?? null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando datos");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (hasRange) fetchData(1); }, [hasRange, dateRange, debouncedSearch, sort]);

  const onSort = (key) => {
    setPagination((p) => ({ ...p, page: 1 }));
    setSort((s) => s.sortBy === key ? { ...s, sortOrder: s.sortOrder === "asc" ? "desc" : "asc" } : { sortBy: key, sortOrder: "desc" });
  };
  const SortIcon = ({ k }) => {
    if (sort.sortBy !== k) return <ArrowUpDown className="w-3 h-3" />;
    return sort.sortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const openRepair = (p) => setRepairModal({ open: true, product: p, newCost: "", saving: false });
  const handleRepair = async () => {
    const cost = parseFloat(repairModal.newCost);
    if (isNaN(cost) || cost < 0) { toast.error("Introduce un coste válido"); return; }
    setRepairModal((m) => ({ ...m, saving: true }));
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(`${API}/products/repair-cost`,
        { platform: repairModal.product.platform || "prestashop", product_uid: repairModal.product.product_uid, correct_unit_cost: cost },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`${res.data.corrected_lines} líneas corregidas · ${res.data.rebuilt_daily_metrics} días recalculados`);
      setRepairModal({ open: false, product: null, newCost: "", saving: false });
      fetchData(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al corregir");
      setRepairModal((m) => ({ ...m, saving: false }));
    }
  };

  const canPrev = pagination.page > 1;
  const canNext = pagination.page < pagination.total_pages;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-products-page">

        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">Rentabilidad por Producto</h1>
              <p className="text-sm text-slate-500">Ingresos totales tienda · Gasto publicidad distribuido entre todas las plataformas (GAds + Meta + Awin)</p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="analytics-products-submenu-links">
            <Button variant="outline" asChild><Link to="/analytics">GA4</Link></Button>
            <Button variant="outline" asChild><Link to="/analytics/brands">Marcas</Link></Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" asChild><Link to="/analytics/products">Productos</Link></Button>
            <Button variant="outline" asChild><Link to="/analytics/google-ads-products">GAds — Productos</Link></Button>
          </div>
        </div>

        {/* Zero-price alert banner */}
        {zeroPriceData?.count > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 overflow-hidden" data-testid="zero-price-alert">
            <button
              onClick={() => setZeroPriceOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-red-100 transition-colors"
              data-testid="zero-price-alert-toggle"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                <span className="text-sm font-semibold text-red-800">
                  {zeroPriceData.count} producto{zeroPriceData.count !== 1 ? "s" : ""} con precio de venta a 0€
                </span>
                <span className="text-xs text-red-600">— Revisa el catálogo para evitar ventas a pérdida</span>
              </div>
              {zeroPriceOpen
                ? <ChevronUp className="w-4 h-4 text-red-500 shrink-0" />
                : <ChevronDown className="w-4 h-4 text-red-500 shrink-0" />}
            </button>
            {zeroPriceOpen && (
              <div className="border-t border-red-200 overflow-x-auto">
                <table className="w-full text-xs" data-testid="zero-price-table">
                  <thead>
                    <tr className="bg-red-100 text-red-700">
                      <th className="text-left px-4 py-2 font-semibold">Referencia</th>
                      <th className="text-left px-4 py-2 font-semibold">Nombre</th>
                      <th className="text-left px-4 py-2 font-semibold">Plataforma</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zeroPriceData.products.slice(0, 50).map((p, i) => (
                      <tr key={p.product_uid || i} className="border-t border-red-100 hover:bg-red-50">
                        <td className="px-4 py-2 font-mono text-slate-700">{p.reference || "—"}</td>
                        <td className="px-4 py-2 text-slate-800 max-w-xs truncate">{p.name || "—"}</td>
                        <td className="px-4 py-2 text-slate-500 capitalize">{p.platform || "—"}</td>
                      </tr>
                    ))}
                    {zeroPriceData.count > 50 && (
                      <tr>
                        <td colSpan={3} className="px-4 py-2 text-red-500 italic text-center">
                          … y {zeroPriceData.count - 50} más
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Controls */}
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <Popover open={calOpen} onOpenChange={(o) => { setCalOpen(o); if (o) setPendingDate(dateRange); }}>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="products-date-picker-button">
                  <Calendar className="w-4 h-4 mr-2" />
                  {hasRange
                    ? `${format(dateRange.from, "d MMM", { locale: es })} – ${format(dateRange.to, "d MMM yyyy", { locale: es })}`
                    : "Selecciona rango de fechas"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex">
                  <div className="border-r border-slate-200 p-2 space-y-1 min-w-[170px]">
                    <p className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">Accesos rápidos</p>
                    {presets.map((p) => (
                      <button key={p.label} onClick={() => { const r = { from: p.from, to: p.to }; setDateRange(r); setPendingDate(r); setCalOpen(false); }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md" data-testid={`products-date-preset-${p.label.toLowerCase().replace(/\s+/g,"-")}`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-2 flex flex-col">
                    <CalendarComponent mode="range" selected={pendingDate} onSelect={(r) => setPendingDate({ from: r?.from || null, to: r?.to || null })} numberOfMonths={2} locale={es} />
                    <div className="border-t pt-2 flex justify-end">
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" disabled={!pendingDate.from}
                        onClick={() => { const r = { from: pendingDate.from, to: pendingDate.to || pendingDate.from }; setDateRange(r); setCalOpen(false); }}>
                        Aplicar
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <div className="relative w-full lg:w-96">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => { setPagination((p) => ({ ...p, page: 1 })); setSearch(e.target.value); }}
                placeholder="Buscar referencia o nombre..." className="pl-9" data-testid="products-search-input" />
            </div>
          </CardContent>
        </Card>

        {!hasRange ? (
          <Card className="bg-amber-50 border border-amber-200">
            <CardContent className="p-6">
              <p className="text-amber-900 font-semibold">Selecciona un rango de fechas para calcular la rentabilidad</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div>
                  <CardTitle className="text-lg font-bold text-slate-900">Tabla de Rentabilidad por Producto</CardTitle>
                  <p className="text-sm text-slate-500">{loading ? "Cargando..." : `${pagination.total_rows} productos`}</p>
                  <p className="text-xs text-slate-400">Gasto Total Dist. = (GAds + Meta + Awin) × (ingresos_sku / ingresos_totales)</p>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {(dataQuality.excluded_sales_lines > 0 || dataQuality.unmatched_google_ads_cost > 0) && (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {dataQuality.excluded_sales_lines > 0 && <p>{dataQuality.excluded_sales_lines} líneas excluidas (sin metadatos de producto).</p>}
                  {dataQuality.unmatched_google_ads_cost > 0 && <p>€{dataQuality.unmatched_google_ads_cost.toFixed(2)} de gasto GAds no mapeado a producto.</p>}
                </div>
              )}

              <div className="overflow-x-auto" data-testid="products-table-wrapper">
                <Table data-testid="products-profitability-table">
                  <TableHeader>
                    <TableRow>
                      {COLUMNS.map((col) => (
                        <TableHead key={col.key}
                          className={`${col.align === "left" ? "text-left" : "text-right"} ${col.key === "_rent_real" ? "bg-slate-50 border-l border-slate-200" : ""}`}>
                          {col.special ? (
                            <span className="font-bold text-slate-800 text-xs">Rentabilidad Real %</span>
                          ) : col.sortable === false ? (
                            <span className="text-slate-600 text-xs">{col.label}</span>
                          ) : (
                            <button type="button" onClick={() => SORT_FIELDS.has(col.key) && onSort(col.key)}
                              className={`inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 ${col.align === "left" ? "" : "justify-end w-full"}`}
                              data-testid={`products-sort-${col.key}`}>
                              {col.label}
                              {SORT_FIELDS.has(col.key) && <SortIcon k={col.key} />}
                            </button>
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!loading && rows.length === 0 && (
                      <TableRow><TableCell colSpan={TOTAL_COLS} className="text-center text-slate-500 py-10">Sin datos para el rango seleccionado.</TableCell></TableRow>
                    )}
                    {rows.map((row, i) => (
                      <TableRow key={row.product_uid || i} data-testid={`products-row-${i}`}>
                        <TableCell className="font-semibold text-slate-900" data-testid={`products-row-${i}-reference`}>{row.reference || "—"}</TableCell>
                        <TableCell className="text-slate-700 max-w-[200px] truncate" data-testid={`products-row-${i}-name`}>{row.name || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-slate-700">{fmtInt(row.visits)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-700">{fmtInt(row.add_to_cart)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-700">{fmtInt(row.sales)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-700">{fmtPct(row.conversion_rate)}</TableCell>
                        <TableCell className={`text-right font-mono ${isNeg(row.revenue) ? "text-red-600" : "text-slate-700"}`}>{fmtEur(row.revenue)}</TableCell>
                        <TableCell className={`text-right font-mono ${isNeg(row.product_cost) ? "text-red-600" : "text-slate-700"}`} data-testid={`products-row-${i}-product-cost`}>
                          <span className="inline-flex items-center gap-1.5">
                            {fmtEur(row.product_cost)}
                            <button type="button" title="Corregir coste unitario" onClick={() => openRepair(row)}
                              className="text-slate-400 hover:text-slate-700 transition-colors" data-testid={`products-row-${i}-repair-cost-btn`}>
                              <Pencil className="w-3 h-3" />
                            </button>
                          </span>
                        </TableCell>
                        <TableCell className={`text-right font-mono ${isNeg(row.gross_profit) ? "text-red-600" : "text-slate-700"}`}>{fmtEur(row.gross_profit)}</TableCell>
                        <TableCell className={`text-right font-mono ${isNeg(row.gross_margin_pct) ? "text-red-600" : "text-slate-700"}`}>{fmtPct(row.gross_margin_pct)}</TableCell>
                        {/* GAds spend (real) */}
                        <TableCell className="text-right font-mono text-slate-500 text-xs">{fmtEur(row.ad_spend)}</TableCell>
                        {/* Distributed spend (all platforms) */}
                        <TableCell className="text-right font-mono text-slate-700">{fmtEur(row.distributed_ad_spend)}</TableCell>
                        <TableCell className={`text-right font-mono font-bold ${isNeg(row.net_profit) ? "text-red-600" : "text-slate-900"}`}>{fmtEur(row.net_profit)}</TableCell>
                        <TableCell className={`text-right font-mono ${isNeg(row.net_margin_pct) ? "text-red-700 bg-red-50" : "text-slate-700"}`}>{fmtPct(row.net_margin_pct)}</TableCell>
                        <TableCell className={`text-right font-mono ${isNeg(row.roas) ? "text-red-600" : "text-slate-700"}`}>{fmtX(row.roas)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-600 text-sm">{row.cross_sell_conversions != null ? Math.round(row.cross_sell_conversions) : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-slate-600 text-sm">{row.cross_sell_revenue > 0 ? fmtEur(row.cross_sell_revenue) : "—"}</TableCell>
                        <TableCell className={`text-right font-mono ${row.true_roas != null && row.true_roas > (row.roas ?? 0) ? "text-green-700 font-semibold" : "text-slate-600"}`}>{fmtX(row.true_roas)}</TableCell>
                        <TableCell className={`text-right font-mono ${row.halo_score != null && row.halo_score > 1 ? "text-blue-600 font-semibold" : "text-slate-500"}`}>{row.halo_score != null ? fmtX(row.halo_score) : "—"}</TableCell>
                        {/* Rentabilidad Real % — distributed spend basis */}
                        <TableCell className="text-right bg-slate-50 border-l border-slate-100" data-testid={`products-row-${i}-rentabilidad-real`}>
                          <RentReal row={row} avgMargin={avgStoreGrossMargin} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {pagination.total_rows > 50 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-slate-500">Página {pagination.page} de {pagination.total_pages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" disabled={!canPrev || loading} onClick={() => { const p = pagination.page - 1; setPagination((pr) => ({ ...pr, page: p })); fetchData(p); }}>Anterior</Button>
                    <Button variant="outline" disabled={!canNext || loading} onClick={() => { const p = pagination.page + 1; setPagination((pr) => ({ ...pr, page: p })); fetchData(p); }}>Siguiente</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Repair cost dialog */}
      <Dialog open={repairModal.open} onOpenChange={(o) => !repairModal.saving && setRepairModal((m) => ({ ...m, open: o }))}>
        <DialogContent className="sm:max-w-md" data-testid="repair-cost-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" />Corregir coste</DialogTitle></DialogHeader>
          {repairModal.product && (
            <div className="space-y-4">
              <div className="rounded-md bg-slate-50 border p-3 space-y-1">
                <p className="text-sm font-semibold" data-testid="repair-product-name">{repairModal.product.name || repairModal.product.reference}</p>
                <p className="text-xs text-slate-500">Ref: {repairModal.product.reference} · {repairModal.product.platform}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="repair-input">Nuevo coste unitario (€/ud)</Label>
                <Input id="repair-input" type="number" min="0" step="0.01" placeholder="ej. 12.50" value={repairModal.newCost}
                  onChange={(e) => setRepairModal((m) => ({ ...m, newCost: e.target.value }))} data-testid="repair-cost-input"
                  onKeyDown={(e) => e.key === "Enter" && handleRepair()} />
              </div>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">Actualiza el coste en <strong>todos los pedidos históricos</strong> y recalcula métricas.</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRepairModal({ open: false, product: null, newCost: "", saving: false })} disabled={repairModal.saving} data-testid="repair-cost-cancel">Cancelar</Button>
            <Button onClick={handleRepair} disabled={repairModal.saving || !repairModal.newCost} className="bg-slate-900 text-white" data-testid="repair-cost-confirm">
              {repairModal.saving ? "Corrigiendo..." : "Aplicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
